// Copy builders for the patient consent page. Kept out of the component so the
// wording can be asserted without rendering React.

import { REQUESTER_TYPE, type RequesterType } from "@/server/constants/persona";

/** Used when the workspace name has not loaded, or the workspace has none. */
const UNKNOWN_REQUESTER = "Your care team";

/** Tail of the requester sentence, shared by every named variant. */
const REQUESTER_TAIL = "uses Medblocks Platform to bring them into one secure place.";

/** What the patient should understand the requester to be, per requester type. */
const REQUESTER_KIND: Record<RequesterType, string> = {
  [REQUESTER_TYPE.PERSON]: "the person",
  [REQUESTER_TYPE.ORGANIZATION]: "the organization",
};

/**
 * Read by the owner of an individual workspace, who is connecting their own
 * records. The heading above it names Medblocks as the party receiving them, so
 * this says how they get here rather than restating who is getting them.
 */
const OWN_RECORDS_LINE =
  "We retrieve your records from each health system you connect, so they are all in one place.";

/**
 * The line naming who is asking for the records, and whether that party is a
 * person or an organization — a name alone cannot be told apart.
 *
 * `ownRecords` is the individual workflow, where the reader is the patient and
 * nobody is requesting anything from them. `requesterType` is null on every
 * workspace created before the column existed, so the kind is left unsaid there
 * rather than guessed at.
 *
 * @param organizationName workspace name, or null while it is unknown
 * @param requesterType person, organization, or null when not recorded
 * @param ownRecords true when the reader is connecting their own records
 * @returns one sentence, ready to render as-is
 */
export function requesterLine(
  organizationName: string | null | undefined,
  requesterType: RequesterType | null | undefined,
  ownRecords = false,
): string {
  if (ownRecords) return OWN_RECORDS_LINE;

  const name = organizationName?.trim();
  if (!name) {
    return `${UNKNOWN_REQUESTER} uses Medblocks Platform to bring your medical records into one secure place.`;
  }
  if (requesterType) {
    return `${name}, ${REQUESTER_KIND[requesterType]} requesting your records, ${REQUESTER_TAIL}`;
  }
  return `${name}, who is requesting your records, ${REQUESTER_TAIL}`;
}
